import { isGYUser } from '../../util/roles';
import { useFormatter } from '../../util/formatter';

const [ formatNumber ] = useFormatter();

export const calculateUsageRatio = (userRoles, discharge_energy, charge_energy) => {
  if(!discharge_energy?.length || !charge_energy?.length) {
    return null;
  }
  if(isGYUser(userRoles)) {
    const usageRatio = discharge_energy.map((discharge, index) => {
      const charge = charge_energy[index];
      let ratio = discharge / charge;
      if(isNaN(ratio)) {
        ratio = 0;
      }
      return ratio;
    });
    return usageRatio;
  }
  return null;
};

export const getP10GraphOpt = ({ p10DateData, p10SoC, p10Power, p10Volt, p10Current, p10GridPower, p10Temp }) => ({
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'cross',
      label: {
        backgroundColor: '#6a7985'
      }
    },
    formatter: function (params) {
      let result = params[0].axisValue + '<br/>';
      params.forEach(function (item) {
        const value = parseFloat(item.value);
        const formattedValue = isNaN(value) ? item.value : value.toFixed(2);
        result += item.marker + item.seriesName + ': ' + formattedValue + '<br/>';
      });
      return result;
    }
  },
  legend: {
    data: ['State of Charge (%)', 'Power (kW)', 'Voltage (V)', 'Current (A)', 'Grid Power (kW)', 'Temperature (°C)'],
    selected: {
      "State of Charge (%)": true,
      "Power (kW)": true,
      "Voltage (V)": false,
      "Current (A)": false,
      "Grid Power (kW)": true,
      "Temperature (°C)": false
    },
    top: 0,
    textStyle: {
      color: '#666'
    }
  },
  grid: {
    left: '3%',
    right: '4%',
    bottom: '3%',
    containLabel: true
  },
  toolbox: {
    feature: {
      saveAsImage: {
        title: 'Save as Image'
      },
      dataZoom: {
        title: {
          zoom: 'Zoom',
          back: 'Reset'
        }
      }
    }
  },
  xAxis: {
    type: 'category',
    data: p10DateData,
    axisTick: {
      alignWithLabel: true,
    },
    axisLabel: {
      color: '#666'
    }
  },
  yAxis: [
    {
      type: 'value',
      name: 'SoC (%)',
      position: 'left',
      offset: 0,
      axisLabel: {
        color: '#666'
      },
      min: Math.round((Math.round((Math.min(...p10Power, ...p10GridPower) / 10)) * 10 - 20) / (Math.round((Math.max(...p10Power, ...p10GridPower) / 10)) * 10 + 20) * 100),
      max: 100,
    },
    {
      type: 'value',
      name: 'Power (kW)',
      position: 'left',
      offset: -60,
      axisLabel: {
        color: '#666'
      },
      min: Math.round((Math.min(...p10Power, ...p10GridPower) / 10)) * 10 - 20,
      max: Math.round((Math.max(...p10Power, ...p10GridPower) / 10)) * 10 + 20,
    },
    {
      type: 'value',
      name: 'Voltage (V)',
      position: 'right',
      offset: 0,
      axisLabel: {
        color: '#666'
      }
    },
    {
      type: 'value',
      name: 'Current (A)',
      position: 'right',
      offset: -80,
      axisLabel: {
        color: '#666'
      }
    }
    ,
    {
      type: 'value',
      name: 'Temperature (°C)',
      position: 'right',
      offset: -180,
      axisLabel: {
        color: '#666'
      }
    }
  ],
  dataZoom: [
    {
      type: 'inside',
      start: 0,
      end: 100
    },
    {
      type: 'slider',
      start: 0,
      end: 100,
    }
  ],
  series: [
    {
      name: 'State of Charge (%)',
      type: 'line',
      yAxisIndex: 0,
      data: p10SoC,
      smooth: true,
      lineStyle: {
        color: '#88b917',
        width: 2
      },
      itemStyle: {
        color: '#88b917'
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [{
            offset: 0, color: 'rgba(136, 185, 23, 0.3)'
          }, {
            offset: 1, color: 'rgba(136, 185, 23, 0.1)'
          }]
        }
      }
    },
    {
      name: 'Power (kW)',
      type: 'line',
      yAxisIndex: 1,
      data: p10Power,
      smooth: true,
      lineStyle: {
        color: '#1890ff',
        width: 2
      },
      itemStyle: {
        color: '#1890ff'
      }
    },
    {
      name: 'Voltage (V)',
      type: 'line',
      yAxisIndex: 2,
      data: p10Volt,
      smooth: true,
      lineStyle: {
        color: '#ff7875',
        width: 2
      },
      itemStyle: {
        color: '#ff7875'
      }
    },
    {
      name: 'Current (A)',
      type: 'line',
      yAxisIndex: 3,
      data: p10Current,
      smooth: true,
      lineStyle: {
        color: '#faad14',
        width: 2
      },
      itemStyle: {
        color: '#faad14'
      }
    },
    {
      name: 'Grid Power (kW)',
      type: 'line',
      yAxisIndex: 1,
      data: p10GridPower,
      hidden: true,
      smooth: true,
      lineStyle: {
        color: '#f28a10',
        width: 2
      },
      itemStyle: {
        color: '#f28a10'
      }
    }
    ,
    {
      name: 'Temperature (°C)',
      type: 'line',
      yAxisIndex: 4,
      data: p10Temp,
      smooth: true,
      lineStyle: {
        color: '#8884d8',
        width: 2
      },
      itemStyle: {
        color: '#8884d8'
      }
    }
  ]
});

export const getCabinetsPowerGraphOpt = ({ p10DateData, cabinetP10 }) => ({
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'cross',
      label: {
        backgroundColor: '#6a7985'
      }
    },
    formatter: function (params) {
      let result = params[0].axisValue + '<br/>';
      params.forEach(function (item) {
        const value = parseFloat(item.value);
        const formattedValue = isNaN(value) ? item.value : value.toFixed(2);
        result += item.marker + item.seriesName + ': ' + formattedValue + '<br/>';
      });
      return result;
    }
  },
  legend: {
    data: Object.keys(cabinetP10).map(cabinetId => `Cabinet ${cabinetId} Power (kW)`),
    top: 0,
    textStyle: {
      color: '#666',
    }
  },
  grid: {
    left: '3%',
    right: '4%',
    bottom: '3%',
    containLabel: true
  },
  toolbox: {
    feature: {
      saveAsImage: {
        title: 'Save as Image'
      },
      dataZoom: {
        title: {
          zoom: 'Zoom',
          back: 'Reset'
        }
      }
    }
  },
  dataZoom: [
    {
      type: 'inside',
      start: 0,
      end: 100
    },
    {
      type: 'slider',
      start: 0,
      end: 100,
    }
  ],
  yAxis: {
    type: 'value',
    name: 'Power (kW)'
  },
  xAxis: {
    type: 'category',
    data: p10DateData,
    axisTick: {
      alignWithLabel: true,
    },
  },
  series: Object.values(cabinetP10).map((cabinetData, index) => ({
    name: 'Cabinet ' + Object.keys(cabinetP10)[index] + ' Power (kW)',
    type: 'line',
    data: cabinetData['power'],
  })),
});

export const getBarGraphOpt = ({ userRoles, dateData, charge_energy, charge_energy_on, charge_energy_off, charge_energy_std, discharge_energy, discharge_energy_on, discharge_energy_off, discharge_energy_std }) => ({
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'cross',
      label: {
        backgroundColor: '#6a7985'
      }
    },
    formatter: function (params) {
      var chargeTotal = 0;
      var dischargeTotal = 0;

      const colors = {
        "Charge On-peak": "#ffc602",
        "Charge Off-peak": "#f28a10",
        "Charge Standard-peak": "#75b8f3",
        "Discharge On-peak": "#ffc602",
        "Discharge Off-peak": "#f28a10",
        "Discharge Standard-peak": "#75b8f3",
        "Usage Ratio (discharge/charge)": "#8884d8"
      }

      const colorSpan = (color) =>
        '<span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:' +
        color +
        '"></span>';
        
      let html = "</br>";

      params.forEach((item) => {
        if(item.seriesName.startsWith("Charge")) {
          chargeTotal += +parseFloat(item.data).toFixed(2);
        }
        if(item.seriesName.startsWith("Discharge")) {
          dischargeTotal += +parseFloat(item.data).toFixed(2);
        }
        html += `<p>${colorSpan(colors[item.seriesName])} ${item.axisValue} ${item.seriesName}: ${formatNumber(item.data, 2)} ${item.seriesName.includes("Charge") || item.seriesName.includes("Discharge") ? "kWh" : ""}</p>`;
      });
      
      html += "<hr style='padding:0; margin:10px 0'/>";
      
      html += `<p><strong>Total Charge Energy: ${chargeTotal.toFixed(2)} kWh</strong></p>`;
      html += `<p><strong>Total Discharge Energy: ${dischargeTotal.toFixed(2)} kWh</strong></p>`;
      return html;
    },
  },
  legend: {
    data: [/*'Charge Energy', */'Charge On-peak', 'Charge Off-peak', 'Charge Standard-peak', /*'Discharge Energy', */'Discharge On-peak', 'Discharge Off-peak', 'Discharge Standard-peak', ...(isGYUser(userRoles) ? ['Usage Ratio (discharge/charge)'] : [])],
    top: 0,
    textStyle: {
      color: '#666',
    }
  },
  grid: {
    left: '3%',
    right: '4%',
    bottom: '3%',
    containLabel: true
  },
  toolbox: {
    feature: {
      saveAsImage: {
        title: 'Save as Image'
      },
      dataZoom: {
        title: {
          zoom: 'Zoom',
          back: 'Reset'
        }
      }
    }
  },
  // remove space between adjacent bar stacks
  barGap: '0%',
  yAxis: [
    {
      type: 'value',
      name: 'Energy (kWh)'
    },
    {
      type: 'value',
      name: 'Ratio',
    }
  ],
  xAxis: {
    type: 'category',
    data: dateData,
    axisTick: {
      alignWithLabel: true,
    },
  },
  series: [
    // {
    //   name: 'Charge Energy',
    //   type: 'line',
    //   data: charge_energy,
    //   yAxisIndex: 0,
    // },
    {
      name: 'Charge On-peak',
      type: 'bar',
      stack: 'charge',
      color: "#ffc602",
      data: charge_energy_on,
      yAxisIndex: 0,
    },
    {
      name: 'Charge Off-peak',
      type: 'bar',
      stack: 'charge',
      color: '#f28a10',
      data: charge_energy_off,
      yAxisIndex: 0,
    },
    {
      name: 'Charge Standard-peak',
      type: 'bar',
      stack: 'charge',
      color: '#75b8f3',
      data: charge_energy_std,
      yAxisIndex: 0,
    },
    // {
    //   name: 'Discharge Energy',
    //   type: 'line',
    //   data: discharge_energy,
    //   yAxisIndex: 0,
    // },
    {
      name: 'Discharge On-peak',
      type: 'bar',
      stack: 'discharge',
      color: '#ffc602',
      data: discharge_energy_on,
      yAxisIndex: 0,
    },
    {
      name: 'Discharge Off-peak',
      type: 'bar',
      stack: 'discharge',
      color: '#f28a10',
      data: discharge_energy_off,
      yAxisIndex: 0,
    },
    {
      name: 'Discharge Standard-peak',
      type: 'bar',
      stack: 'discharge',
      color: '#75b8f3',
      data: discharge_energy_std,
      yAxisIndex: 0,
    },
    ...(isGYUser(userRoles) ? [{
      name: 'Usage Ratio (discharge/charge)',
      type: 'line',
      data: calculateUsageRatio(userRoles, discharge_energy, charge_energy),
      yAxisIndex: 1,
      smooth: true,
      lineStyle: {
        color: '#8884d8',
        width: 2
      },
      itemStyle: {
        color: '#8884d8'
      }
    }] : [])
  ],
});